import React, { ReactNode, useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import './Patterns.css';

export interface FormShellSection {
  id: string;
  title: string;
}

export interface FormShellProps {
  /** When provided, renders the long-form layout with a TOC. Empty array (or undefined) renders short-form layout (no TOC, single content card column). */
  sections?: FormShellSection[];
  /** Form section content (typically <FormSection> components). */
  children: ReactNode;
  /** Sticky save bar; pass null to omit. */
  saveBar: ReactNode;
  /** When provided, wraps the content in a <form> element so Enter-key submit triggers onSubmit. */
  onSubmit?: (e: React.FormEvent) => void;
}

const FormShell: React.FC<FormShellProps> = ({ sections, children, saveBar, onSubmit }) => {
  const { t } = useTranslation();
  const isLongForm = Boolean(sections && sections.length > 1);
  const [activeId, setActiveId] = useState<string>(() => sections?.[0]?.id ?? '');
  const [sheetOpen, setSheetOpen] = useState(false);

  useEffect(() => {
    if (!sheetOpen) return;
    function onKey(e: KeyboardEvent) {
      if (e.key === 'Escape') setSheetOpen(false);
    }
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [sheetOpen]);

  useEffect(() => {
    if (!isLongForm || !sections || sections.length === 0) return;
    // Scroll-spy: the active section is the LAST one whose top edge has
    // scrolled past a small offset from the viewport top. For a short form
    // where every section fits in the viewport at once, the first section
    // stays active until the user scrolls past it. For long forms, the
    // top-of-viewport section becomes active as the user scrolls down.
    const ANCHOR_OFFSET = 80;
    const compute = () => {
      let active = sections[0].id;
      for (const s of sections) {
        const el = document.getElementById(s.id);
        if (!el) continue;
        if (el.getBoundingClientRect().top - ANCHOR_OFFSET <= 0) active = s.id;
      }
      setActiveId(active);
    };
    // Defer the initial compute to a rAF so the useState initializer's
    // "first section is active" survives mount in environments without
    // real layout (jsdom returns all-zero bounding rects, which would
    // otherwise let every section pass the threshold and the last one win).
    let rafId: number | undefined;
    if (typeof window !== 'undefined' && typeof window.requestAnimationFrame === 'function') {
      rafId = window.requestAnimationFrame(compute);
    }
    window.addEventListener('scroll', compute, { passive: true });
    window.addEventListener('resize', compute);
    return () => {
      if (rafId !== undefined) window.cancelAnimationFrame(rafId);
      window.removeEventListener('scroll', compute);
      window.removeEventListener('resize', compute);
    };
  }, [isLongForm, sections]);

  if (!isLongForm) {
    // Short-form variant: no TOC, single column.
    return (
      <div className="pat-form-shell pat-form-shell--short" data-testid="form-shell">
        {onSubmit ? (
          <form
            className="pat-form-shell__content"
            onSubmit={onSubmit}
          >
            {children}
          </form>
        ) : (
          <div className="pat-form-shell__content">{children}</div>
        )}
        {saveBar}
      </div>
    );
  }

  const activeIndex = sections!.findIndex(s => s.id === activeId);
  const activeSection = sections![activeIndex] ?? sections![0];

  return (
    <div className="pat-form-shell pat-form-shell--long" data-testid="form-shell">
      {/* Mobile TOC pill — only visible on phone via CSS */}
      <button
        type="button"
        className="pat-form-shell__toc-pill"
        data-testid="form-shell-toc-pill"
        onClick={() => setSheetOpen(o => !o)}
        aria-expanded={sheetOpen}
      >
        <span className="pat-form-shell__toc-pill-step">{activeIndex + 1}</span>
        <span className="pat-form-shell__toc-pill-title">{activeSection.title}</span>
        <span className="pat-form-shell__toc-pill-counter">{t('patterns.formShell.stepCounter', { current: activeIndex + 1, total: sections!.length })}</span>
        <i className="bi bi-chevron-down" aria-hidden="true" />
      </button>
      {sheetOpen && (
        <div className="pat-form-shell__toc-sheet" data-testid="form-shell-toc-sheet" role="dialog" aria-modal="true">
          <div className="pat-form-shell__toc-sheet-backdrop" onClick={() => setSheetOpen(false)} aria-hidden="true" />
          <div className="pat-form-shell__toc-sheet-content">
            <h4 className="pat-form-shell__toc-heading">{t('patterns.formShell.sectionsHeading')}</h4>
            {sections!.map((section, index) => {
              const isActive = section.id === activeId;
              return (
                <a
                  key={section.id}
                  href={`#${section.id}`}
                  className={`pat-form-shell__toc-link${isActive ? ' pat-form-shell__toc-link--active' : ''}`}
                  onClick={() => setSheetOpen(false)}
                >
                  <span className="pat-form-shell__toc-step">{index + 1}</span>
                  {section.title}
                </a>
              );
            })}
          </div>
        </div>
      )}
      <aside className="pat-form-shell__toc">
        <div className="pat-form-shell__toc-inner">
          <h4 className="pat-form-shell__toc-heading">{t('patterns.formShell.sectionsHeading')}</h4>
          {sections!.map((section, index) => {
            const isActive = section.id === activeId;
            return (
              <a
                key={section.id}
                href={`#${section.id}`}
                className={`pat-form-shell__toc-link${isActive ? ' pat-form-shell__toc-link--active' : ''}`}
              >
                <span className="pat-form-shell__toc-step">{index + 1}</span>
                {section.title}
              </a>
            );
          })}
        </div>
      </aside>
      {onSubmit ? (
        <form
          className="pat-form-shell__content"
          onSubmit={onSubmit}
        >
          {children}
        </form>
      ) : (
        <div className="pat-form-shell__content">{children}</div>
      )}
      {saveBar && <div className="pat-form-shell__save-bar-slot">{saveBar}</div>}
    </div>
  );
};

export default FormShell;
